import { APPRAISAL_QUERY_TAB_LICENSE_PLATE } from 'site-modules/shared/components/appraisal/appraisal-tabs/constants';
import { validateAppraisalQueryTab } from './validate-appraisal-query-tab';
import { isInternallyReferred } from './is-internally-referred';

/**
 * Determines if the license plate tab should be the default tab.
 * This is based on query parameters, page type, and referrer.
 *
 * @param {Object} options - Configuration options
 * @param {string|null} options.validatedTab - The validated tab from query string (from validateAppraisalQueryTab)
 * @param {boolean} options.isLicensePlateTabDefault - Whether license plate is configured as default
 * @param {boolean} options.isSellCar - Whether this is a sell car page
 * @param {string} options.referer - The HTTP referer URL
 * @returns {boolean} True if license plate tab should be default, false otherwise
 */
export const shouldUseLicensePlateTabAsDefault = ({ validatedTab, isLicensePlateTabDefault, isSellCar, referer }) => {
  const isLpQueryTab = validatedTab === APPRAISAL_QUERY_TAB_LICENSE_PLATE;
  const isNoQueryTab = validatedTab === null;
  const isInternallyReferredPage = isInternallyReferred({ isSellCar, referer });

  return isLpQueryTab || (isNoQueryTab && (isLicensePlateTabDefault || isInternallyReferredPage));
};

/**
 * Initializes tab configuration based on location query parameters, page settings, and referrer.
 * This utility consolidates the logic for determining which tabs to show and which should be default.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.tab - The tab parameter from query string
 * @param {boolean} options.isLicensePlateTabDefault - Whether license plate tab is configured as default
 * @param {boolean} options.isSellCar - Whether this is a sell car page
 * @param {string} options.referer - The HTTP referer URL
 * @returns {Object} Tab initialization results
 * @returns {string|null} returns.validatedTab - The validated tab value or null
 * @returns {boolean} returns.isLicensePlateTabDefaulted - Whether license plate should be the default tab
 */
export const initializeTabConfiguration = ({ tab, isLicensePlateTabDefault, isSellCar, referer }) => {
  const validatedTab = validateAppraisalQueryTab(tab);
  const isLicensePlateTabDefaulted = shouldUseLicensePlateTabAsDefault({
    validatedTab,
    isLicensePlateTabDefault,
    isSellCar,
    referer,
  });

  return {
    validatedTab,
    isLicensePlateTabDefaulted,
  };
};
